/**
 * The Errors chart's columns (19/10/2026): today by the Danish hour, a
 * range by run day — each column split by tone (critical · error · read
 * failed) with the kinds behind it for the hover card.
 * For Media Devoted | Created By Ciro Autiero
 */
import type { DropGenErrorRow, DropGenErrorTone, DropGenErrorsReport } from '@trafficpulse/shared';

import { instantDk } from '../../../utils/clock/dk-label';
import { dayOf } from '../errors-facts';
import { kindWords } from '../errors-words';

export interface ErrorBucket {
  key: string;
  /** The axis word: "09" for an hour, "14/10" for a day. */
  label: string;
  /** The hover card's first line. */
  title: string;
  total: number;
  byTone: Record<DropGenErrorTone, number>;
  byKind: Record<string, number>;
}

const empty = (key: string, label: string, title: string): ErrorBucket => ({ key, label, title, total: 0, byTone: { critical: 0, error: 0, warning: 0 }, byKind: {} });

const add = (b: ErrorBucket, row: DropGenErrorRow) => {
  b.total += 1;
  b.byTone[kindWords(row.kind).tone] += 1;
  b.byKind[row.kind] = (b.byKind[row.kind] ?? 0) + 1;
};

/** Today: 24 Danish hours. A range: one column per run day of the report. */
export function errorBuckets(report: Pick<DropGenErrorsReport, 'from' | 'to' | 'byDay'>, rows: DropGenErrorRow[], days: number, tz: string | null): ErrorBucket[] {
  if (days === 1) {
    const hours = Array.from({ length: 24 }, (_, h) => empty(String(h), String(h).padStart(2, '0'), `${String(h).padStart(2, '0')}:00 Danish time`));
    for (const row of rows) if ((row.runDate ?? dayOf(row.at, tz)) === report.to) add(hours[Number(instantDk(row.at).label.slice(0, 2))], row);
    return hours;
  }
  const byDay = new Map(report.byDay.map((d) => [d.date, empty(d.date, `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`, `${d.date.slice(8, 10)}/${d.date.slice(5, 7)}`)]));
  for (const row of rows) {
    const b = byDay.get(row.runDate ?? dayOf(row.at, tz));
    if (b) add(b, row);
  }
  return [...byDay.values()];
}

/** "1 critical · 2 errors · 1 read failed". */
export const toneLine = (b: ErrorBucket): string =>
  [b.byTone.critical > 0 ? `${b.byTone.critical} critical` : null, b.byTone.error > 0 ? `${b.byTone.error} error${b.byTone.error === 1 ? '' : 's'}` : null, b.byTone.warning > 0 ? `${b.byTone.warning} read${b.byTone.warning === 1 ? '' : 's'} failed` : null]
    .filter(Boolean)
    .join(' · ');
